"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.StorageType = exports.DEFAULT_CACHE_KEY_NAME = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _isSSR = require("../utils/isSSR");
let StorageType = exports.StorageType = /*#__PURE__*/function (StorageType) {
  StorageType["SESSION"] = "sessionStorage";
  StorageType["LOCAL"] = "localStorage";
  return StorageType;
}({});
const DEFAULT_CACHE_KEY_NAME = exports.DEFAULT_CACHE_KEY_NAME = 'stencil_cache';

/**
 * Simple cache that uses ES6 maps to set and get cache with optional device storage
 * Create an instance to start using it
 */
class Cache {
  /**
   * @param option.key the unique cache key to set the value at
   * @param option.value the value to be set in the cache
   * @param option.ttl (defaults to -1 which means no Time to Live) the Time to Live in milliseconds where after it passes, the cache key will yield no value. It'll be used when no TTL is provided to the getter
   * @param option.storageKey the unique string for the storage record. Defaults to 'cache'
   * @param option.storageType either sessionStorage or localStorage
   */
  constructor() {
    let {
      key,
      value,
      ttl,
      storageKey = DEFAULT_CACHE_KEY_NAME,
      storageType
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      storageKey: DEFAULT_CACHE_KEY_NAME
    };
    (0, _defineProperty2.default)(this, "cache", new Map());
    (0, _defineProperty2.default)(this, "storageKey", void 0);
    (0, _defineProperty2.default)(this, "storage", void 0);
    this.storageKey = storageKey;
    const isServerSideRendered = (0, _isSSR.isSSR)();
    if (!isServerSideRendered && !!storageType) {
      this.storage = window[storageType];
    } else if (isServerSideRendered && !!storageType) {
      console.warn("'window' object is not available! ".concat(storageType, " device storage will NOT be used. This might be due to SSR."));
    }
    if (key !== undefined) {
      this.setCache(key, value, ttl);
    }
  }

  /**
   * Fetches the cache from the device storage at the set storageKey and parses it
   * @returns the cacheMap from storage or undefined
   */
  _getStorage() {
    try {
      var _this$storage;
      const cacheFromStorage = (_this$storage = this.storage) === null || _this$storage === void 0 ? void 0 : _this$storage.getItem(this.storageKey);
      return new Map(JSON.parse(cacheFromStorage || '[]'));
    } catch (error) {
      console.error('Failed while attempting to parse retrieved cache from storage at _getStorage', error);
    }
    return;
  }
  /**
   * Saves the cache to the device storage at the set storageKey
   * @param cacheMap the key value cache map to save in the device storage
   */
  _setStorage(cacheMap) {
    var _this$storage2;
    (_this$storage2 = this.storage) === null || _this$storage2 === void 0 || _this$storage2.setItem(this.storageKey, JSON.stringify(Array.from((cacheMap === null || cacheMap === void 0 ? void 0 : cacheMap.entries()) || [])));
  }

  /**
   * Gets the cache from device storage and fetches the value at a specific key
   * @param key the unique cache key to get the value at
   * @returns the value at the given key
   */
  _getKeyFromStorage(key) {
    const cacheMap = this._getStorage();
    return cacheMap === null || cacheMap === void 0 ? void 0 : cacheMap.get(key);
  }

  /**
   * Saves the given key and value in the device storage
   * @param key the unique cache key to set the value at
   * @param value the value to be set in the cache
   * @param ttl the Time to Live in milliseconds where after it passes, the cache key will yield no value. It'll be used when no TTL is provided to the getter
   * @param timeSet when to start counting up to see if the cache is valid
   */
  _setInStorage(key, value, ttl, timeSet) {
    const cacheMap = this._getStorage();
    cacheMap === null || cacheMap === void 0 || cacheMap.set(key, {
      value,
      ttl,
      timeSet
    });
    this._setStorage(cacheMap);
  }
  validateTtl(ttl) {
    if (ttl < -1) {
      console.warn('Bad input for TTL! TTL needs to be a positive number, zero, or -1', ttl);
    }
  }

  /**
   * Sets the cache on the instance
   * @param key the unique cache key to set the value at
   * @param value the value to be set in the cache
   * @param ttl (defaults to -1 which means no Time to Live) the Time to Live in milliseconds where after it passes, the cache key will yield no value. It'll be used when no TTL is provided to the getter
   * @param shouldSaveToStorage wether or not to save the data in browser storage
   */
  setCache(key, value) {
    let ttl = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : -1;
    let shouldSaveToStorage = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    this.validateTtl(ttl);
    const timeSet = Date.now();
    this.cache.set(key, {
      value,
      ttl,
      timeSet
    });
    !!shouldSaveToStorage && this._setInStorage(key, value, ttl, timeSet);
  }

  /**
   * Gets the value at the given cache key for the passed in, or originally set TTL on the cache map itself
   * @param key the unique cache key to set the value at
   * @param uniqueTtl in milliseconds
   * @param shouldGetFromStorage wether or not to get the data from browser storage
   * @returns undefined or cachedData
   */
  getCache(key, uniqueTtl) {
    let shouldGetFromStorage = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    uniqueTtl !== undefined && this.validateTtl(uniqueTtl);
    const shouldLookAtStorage = this.cache.get(key) === undefined && shouldGetFromStorage;
    const valueObjFromStorage = shouldLookAtStorage ? this._getKeyFromStorage(key) : undefined;

    // Since we are using the storage cache as a back up, and in memory cache is more performant
    // we store the cache back into memory when it's missing in memory but present in storage
    const shouldSaveBackToMemory = shouldLookAtStorage && !!valueObjFromStorage;
    shouldSaveBackToMemory && this.cache.set(key, valueObjFromStorage);
    const valueObj = this.cache.get(key);
    if (!valueObj) {
      return;
    }
    const {
      ttl,
      timeSet,
      value
    } = valueObj;
    const ttlToUse = uniqueTtl || ttl;
    const timeNow = Date.now();
    const hasCacheExpired = ttlToUse === -1 ? false : timeSet + ttlToUse < timeNow;
    if (hasCacheExpired) {
      return;
    }

    // cache has NOT expired return the cached value
    return value;
  }

  /**
   * Clears cache at given key
   * @param key key to clear cache at
   * @param shouldGetFromStorage wether or not to clear the data from browser storage
   */
  clearCache(key) {
    let shouldClearStorage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    this.cache.delete(key);
    if (shouldClearStorage) {
      const cacheMap = this._getStorage();
      if (cacheMap !== null && cacheMap !== void 0 && cacheMap.has(key)) {
        cacheMap === null || cacheMap === void 0 || cacheMap.delete(key);
        this._setStorage(cacheMap);
      }
    }
  }

  /**
   * Clears all cache
   * @param shouldGetFromStorage wether or not to clear the data from browser storage
   */
  clearAllCache() {
    let shouldClearStorage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    this.cache.clear();
    shouldClearStorage && this._setStorage(new Map());
  }
}
exports.default = Cache;